import React, { useEffect } from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import MoneyInput from "../../input/MoneyInput";
import { DisabilityEmployerFormValues } from "../../disability/DisabilityEmployerFormValues";
import Checkbox from "../../input/Checkbox";
import { useAuth } from "oidc-react";
import { EmployerFormValues } from "../../common/EmployerFormValues";
import { DateTime } from "luxon";
import { DisabilityProduct } from "../../enums/DisabilityProduct";
import { PfmlEmployerFormValues } from "../../pfml/PfmlEmployerFormValues";
import { useFormikContext } from "formik";
import { usePolicy } from "../../../hooks/usePolicy";
import { DefinitionOfEarningsEnum, ShortTermDisabilityPolicyCoverage } from "../../../generated/graphql";
import { useWageAsOfDate } from "../../../hooks/useWageAsOfDate";
import { getValueByKey } from "../../enums/KeyFinder";
import { PayFrequency } from "../../enums/PayFrequency";
import { useMember } from "../../../hooks/useMember";
import SelectDropdown from "../../input/SelectDropdown";
import DateInput from "../../input/DateInput";
import PercentageInput from "../../input/PercentageInput";
import { findProductFor } from "../../enums/FederationProduct";


const Earnings = () => {
    const { values, setFieldValue } = useFormikContext<EmployerFormValues>();
    const auth = useAuth();

    const policyNumber = values.policyNumber;
    const privacyId = values.privacyId;
    const dateLastWorked = values.dateLastWorked as string;
    const disabilityProduct = (values as DisabilityEmployerFormValues)?.product as DisabilityProduct;
    const product = findProductFor(disabilityProduct);
    const { data: wageAsOfDateData } = useWageAsOfDate(policyNumber, privacyId, dateLastWorked, product, dateLastWorked, auth)
    const wageAsOfDate = wageAsOfDateData?.findWageAsOfDate?.wageAsOfDate
    const { data: policyData } = usePolicy(policyNumber, privacyId, wageAsOfDate, auth)
    const policy = policyData?.findPolicy
    const { data: memberData } = useMember(policyNumber, privacyId, wageAsOfDate, auth)
    const member = memberData?.findMember

    const earningsQuarters = (values as any as PfmlEmployerFormValues)?.earningsQuarters;
    const policyCoverage = (policy?.memberGroups?.[0]?.coverages as any)?.[product as string] as ShortTermDisabilityPolicyCoverage;

    const bonusUpQuestion = findBonusUpQuestionFor(policyCoverage?.configuration?.definitionOfEarnings);

    useEffect(() => {
        const underwritingInformation = member?.underwritingInformation;
        if(!underwritingInformation) {
            return;
        }
        setFieldValue('baseWage', underwritingInformation?.wage?.toString());
        setFieldValue('baseWageFrequency', getValueByKey(PayFrequency, underwritingInformation?.wageFrequency));
        setFieldValue('baseWageEffectiveDate', underwritingInformation?.wageEffectiveDate);
    }, [setFieldValue, member]);

    useEffect(() => {
        if(!earningsQuarters?.length || !dateLastWorked) {
            return;
        }
        const initialQuarter = DateTime.fromFormat(dateLastWorked, 'MM/dd/yyyy').startOf('quarter')
        earningsQuarters?.forEach((_, index) => {
            const currentStartOfQuarter = initialQuarter.minus({ quarters: earningsQuarters.length - index })
            const startDate = currentStartOfQuarter.toFormat('MM/dd/yyyy');
            setFieldValue(`earningsQuarters[${index}].startDate`, startDate)
            const endDate = currentStartOfQuarter.endOf('quarter').toFormat('MM/dd/yyyy');
            setFieldValue(`earningsQuarters[${index}].endDate`, endDate)
        })
    }, [earningsQuarters, dateLastWorked, setFieldValue]);

    return (
        <QuestionPanel headerText="What were your employee's earnings before they stopped working?"
                       panelContent={<>
                           <div>
                               <MoneyInput id="base-wage" label={`Employee's base pay on ${wageAsOfDate}`} required={true}/>
                               <SelectDropdown id="base-wage-frequency" label="Pay frequency of base pay provided above" required={true}
                                               options={Object.keys(PayFrequency).map(frequency => ({
                                                   id: frequency.replace(/_/g, '-').toLowerCase(),
                                                   text: (PayFrequency as any)[frequency]
                                               }))}/>
                               <DateInput id="base-wage-effective-date" label="Effective date of pay rate provided above"
                                          required={true}/>
                               <Checkbox id="employee-ownership" label="My employee has ownership in my organization"/>
                               {(values as DisabilityEmployerFormValues)?.employeeOwnership
                                   && <PercentageInput id="employee-ownership-percentage" label="Ownership percentage" required={true}/>}
                               {buildEarningsQuestions()}
                               {bonusUpQuestion && <Checkbox id="bonuses-or-commissions" label={bonusUpQuestion}/>}
                           </div>
                       </>}>
        </QuestionPanel>
    )


    function buildEarningsQuestions() {
        const earningsQuarters = (values as PfmlEmployerFormValues).earningsQuarters;
        if(!earningsQuarters?.length) {
            return null;
        }
        return <>
            {earningsQuarters.map((earningsQuarterDates, index) => {
                return (
                    <MoneyInput
                        id={`earnings-quarters-amount-${index}`}
                        name={`earningsQuarters[${index}].amount`}
                        label={`Employee's gross earnings from ${earningsQuarterDates.startDate} to ${earningsQuarterDates.endDate}`}
                        required={true}
                    />
                )
            })}
        </>
    }
};

function findBonusUpQuestionFor(definitionOfEarnings?: DefinitionOfEarningsEnum | null) {
    if (definitionOfEarnings === DefinitionOfEarningsEnum.BaseWageWithBonus) {
        return "Does your employee earn bonus(es)?"
    } else if (definitionOfEarnings === DefinitionOfEarningsEnum.BaseWageWithCommission) {
        return "Does your employee earn commission(s)?"
    } else if (definitionOfEarnings === DefinitionOfEarningsEnum.BaseWageWithBonusAndCommission) {
        return "Does your employee earn bonus(es) or commission(s)?"
    }
    return null;
}

export default Earnings;